import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <MainLayout>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-50 via-white to-orange-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl flex items-center justify-center shadow-lg">
                <span className="text-white text-2xl font-bold">AI</span>
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-purple-800">
                把初稿整理成可拍短剧
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              这里负责把你已经有的灵感或初稿，整理成标题、人物关系、三集推进、第1集正文和分镜草稿。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/create"
                className="px-8 py-4 bg-gradient-to-r from-purple-600 to-purple-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                整理一版初稿
              </Link>
              <Link
                to="/explore"
                className="px-8 py-4 bg-white text-purple-600 font-semibold rounded-xl shadow-lg border border-purple-200 hover:bg-purple-50 transition-all duration-200"
              >
                看现成案例
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-left max-w-5xl mx-auto">
              <div className="rounded-2xl bg-white/80 border border-purple-100 p-5 shadow-sm">
                <div className="text-sm font-semibold text-purple-600 mb-2">本周常用</div>
                <p className="text-gray-900 font-medium mb-2">咖啡馆重逢 / 时间胶囊 / 合同恋人</p>
                <p className="text-sm text-gray-600">先出一版，再回到这里拆角色和分镜，效率最高。</p>
              </div>
              <div className="rounded-2xl bg-white/80 border border-orange-100 p-5 shadow-sm">
                <div className="text-sm font-semibold text-orange-600 mb-2">最近更新</div>
                <p className="text-gray-900 font-medium mb-2">支持整篇粘贴整理</p>
                <p className="text-sm text-gray-600">免费工具生成的长稿，直接贴回来也能继续落成项目。</p>
              </div>
              <div className="rounded-2xl bg-white/80 border border-green-100 p-5 shadow-sm">
                <div className="text-sm font-semibold text-green-600 mb-2">本月重点</div>
                <p className="text-gray-900 font-medium mb-2">角色卡 + 三集推进 + 分镜草稿</p>
                <p className="text-sm text-gray-600">先把内容资产沉下来，后面改稿和拍摄都更顺。</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              一条链路把初稿落下来
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              重点不是多一个生成按钮，而是把零散内容整理成真正可继续用的短剧项目。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* AI剧本生成 */}
            <div
              onClick={() => handleNavigate('/create')}
              className="bg-gradient-to-br from-purple-50 to-white p-8 rounded-2xl shadow-lg border border-purple-100 hover:shadow-xl transition-all duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center mb-4">
                <span className="text-white text-xl">📝</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">整理初稿</h3>
              <p className="text-gray-600 mb-4">
                把豆包、Kimi、通义或你自己的初稿整理成标题、卖点、三集推进和正文
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• 支持整篇粘贴</li>
                <li>• 自动整理结构</li>
                <li>• 直接落到项目里</li>
              </ul>
            </div>

            {/* 角色设定 */}
            <div
              onClick={() => handleNavigate('/character-setting')}
              className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-2xl shadow-lg border border-orange-100 hover:shadow-xl transition-all duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mb-4">
                <span className="text-white text-xl">👥</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">角色设定</h3>
              <p className="text-gray-600 mb-4">
                从剧本里抽人物，补齐性格、背景和关系，后面写续集不用反复重建
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• 人物档案沉淀</li>
                <li>• 关系线更清楚</li>
                <li>• 可继续复用</li>
              </ul>
            </div>

            {/* 分镜设计 */}
            <div
              onClick={() => handleNavigate('/storyboard')}
              className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl shadow-lg border border-green-100 hover:shadow-xl transition-all duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-4">
                <span className="text-white text-xl">🎬</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">分镜设计</h3>
              <p className="text-gray-600 mb-4">
                把正文继续拆成镜头、场景和备注，不再停留在一整坨文案
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• 场景切分更快</li>
                <li>• 镜头备注可改</li>
                <li>• 后续拍摄更顺</li>
              </ul>
            </div>

            {/* 智能编辑 */}
            <div
              onClick={() => handleNavigate('/create')}
              className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl shadow-lg border border-blue-100 hover:shadow-xl transition-all duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
                <span className="text-white text-xl">✏️</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">正文细修</h3>
              <p className="text-gray-600 mb-4">
                不管是自己写的，还是免费工具给的，都能继续修钩子、对白和节奏
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• 正文复制导出</li>
                <li>• 继续改对白</li>
                <li>• 少一点模板感</li>
              </ul>
            </div>

            {/* 多媒体生成 */}
            <div
              onClick={() => handleNavigate('/resource-center')}
              className="bg-gradient-to-br from-pink-50 to-white p-8 rounded-2xl shadow-lg border border-pink-100 hover:shadow-xl transition-all duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 bg-pink-500 rounded-xl flex items-center justify-center mb-4">
                <span className="text-white text-xl">🎭</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">资源参考</h3>
              <p className="text-gray-600 mb-4">
                模板、教程和案例放在一起，先找方向，再回到项目里继续做
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• 真内容可打开</li>
                <li>• 模板可直接套用</li>
                <li>• 案例能回看</li>
              </ul>
            </div>

            {/* 项目管理 */}
            <div
              onClick={() => handleNavigate('/projects')}
              className="bg-gradient-to-br from-indigo-50 to-white p-8 rounded-2xl shadow-lg border border-indigo-100 hover:shadow-xl transition-all duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center mb-4">
                <span className="text-white text-xl">📁</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">项目管理</h3>
              <p className="text-gray-600 mb-4">
                剧本、角色和分镜放在同一条线上，后面修改和回看都不会散
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• 项目内容沉淀</li>
                <li>• 版本更好回看</li>
                <li>• 后续迭代更顺</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 热门短剧推荐 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              最近整理案例
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              这些不是空壳示例，而是已经能继续拆角色、改正文和做分镜的项目底稿。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* 短剧卡片1 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group">
              <div className="relative">
                <div className="aspect-video bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition">
                      <span className="text-white text-2xl">▶️</span>
                    </div>
                    <p className="text-white font-semibold">立即观看</p>
                  </div>
                </div>
                <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                  3:20
                </div>
                <div className="absolute top-4 left-4 bg-purple-600 text-white px-3 py-1 rounded-full text-sm">
                  互动剧情
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">时间胶囊</h3>
                <p className="text-gray-600 mb-4">一个关于时间旅行者试图改变历史的故事，每个选择都可能影响未来。</p>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>👁 12.5k</span>
                    <span>❤️ 892</span>
                    <span>💬 156</span>
                  </div>
                  <span className="text-sm text-purple-600 font-semibold">科幻</span>
                </div>
                <Link
                  to="/player/1"
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-lg transition text-center block"
                >
                  开始观看
                </Link>
              </div>
            </div>

            {/* 短剧卡片2 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group">
              <div className="relative">
                <div className="aspect-video bg-gradient-to-br from-pink-600 to-pink-800 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition">
                      <span className="text-white text-2xl">▶️</span>
                    </div>
                    <p className="text-white font-semibold">立即观看</p>
                  </div>
                </div>
                <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                  2:50
                </div>
                <div className="absolute top-4 left-4 bg-pink-600 text-white px-3 py-1 rounded-full text-sm">
                  互动剧情
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">咖啡馆的邂逅</h3>
                <p className="text-gray-600 mb-4">都市爱情故事，关于缘分和选择，你的每个决定都会影响故事的走向。</p>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>👁 8.9k</span>
                    <span>❤️ 756</span>
                    <span>💬 124</span>
                  </div>
                  <span className="text-sm text-pink-600 font-semibold">爱情</span>
                </div>
                <Link
                  to="/player/2"
                  className="w-full bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 rounded-lg transition text-center block"
                >
                  开始观看
                </Link>
              </div>
            </div>

            {/* 短剧卡片3 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group">
              <div className="relative">
                <div className="aspect-video bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition">
                      <span className="text-white text-2xl">🎬</span>
                    </div>
                    <p className="text-white font-semibold">立即观看</p>
                  </div>
                </div>
                <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                  3:40
                </div>
                <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
                  AI生成
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">古风探案</h3>
                <p className="text-gray-600 mb-4">古代探案故事，需要观众协助破案，体验推理的乐趣。</p>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>👁 9.6k</span>
                    <span>❤️ 734</span>
                    <span>🎯 悬疑</span>
                  </div>
                </div>
                <Link
                  to="/player/3"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition text-center block"
                >
                  开始观看
                </Link>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/explore"
              className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-semibold rounded-xl shadow-lg hover:bg-purple-700 transition-all duration-200"
            >
              探索更多短剧
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* 互动体验特色 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              这个网页真正有用的地方
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              不是再做一个聊天页，而是把短剧内容整理成后面能继续用的资产。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎭</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">初稿结构化</h3>
              <p className="text-gray-600">一段灵感也好，一整篇长稿也好，先整理成标题、人物、推进和正文。</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🤖</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">角色可沉淀</h3>
              <p className="text-gray-600">角色信息会留在项目里，后面补写续集、改关系、换方向都更省力。</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎬</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">分镜能继续落</h3>
              <p className="text-gray-600">不是只给你一篇文案，后面还能继续拆到镜头和场景备注。</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📱</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">项目不再散</h3>
              <p className="text-gray-600">剧本、角色、分镜和资源都在一条链路里，后续接拍摄更顺。</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-purple-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            先把一版初稿落下来
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            先用免费工具出第一稿，或者直接用你自己的 API，在这里继续整理成角色、分镜和项目。
          </p>
          <Link
            to="/create"
            className="inline-flex items-center px-8 py-4 bg-white text-purple-600 font-semibold rounded-xl shadow-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-200"
          >
            去整理初稿
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </section>

    </MainLayout>
  );
};

export default HomePage;
